import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/ui/page-header';
import { LinkedInProfileService, LinkedInProfileData } from '@/services/linkedinProfileService';
import { Plus, Copy, Edit, Trash2, Calendar, Building, Users } from 'lucide-react';
import { toast } from 'sonner';

const USE_CASES = [
  'Job Search',
  'Networking',
  'Personal Branding',
  'Business Development',
  'Thought Leadership',
  'Career Change',
  'Other'
];

export default function LinkedInProfileLibrary() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<LinkedInProfileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewProfileDialog, setShowNewProfileDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedProfileForDuplicate, setSelectedProfileForDuplicate] = useState<string | null>(null);
  const [newProfileData, setNewProfileData] = useState({
    name: '',
    description: '',
    useCase: ''
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const data = await LinkedInProfileService.getAllProfiles();
      // Filter to only show profiles that have actual content
      const profilesWithContent = data.filter(profile => {
        const hasContent = 
          profile.generated_profile?.rawContent ||
          profile.generated_report_data?.rawContent ||
          profile.raw_content_data?.rawContent;
        return !!hasContent;
      });
      setProfiles(profilesWithContent);
    } catch (error) {
      console.error('Error loading profiles:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = () => {
    if (!newProfileData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    const queryParams = new URLSearchParams({
      profileName: newProfileData.name,
      description: newProfileData.description,
      useCase: newProfileData.useCase
    });

    navigate(`/linkedin-wizard?${queryParams.toString()}`);
  };

  const handleDuplicateProfile = async () => {
    if (!selectedProfileForDuplicate || !newProfileData.name.trim()) {
      toast.error('Profile name is required');
      return;
    }

    try {
      await LinkedInProfileService.duplicateProfile(selectedProfileForDuplicate, newProfileData.name);
      toast.success('Profile duplicated successfully');
      setShowDuplicateDialog(false);
      setSelectedProfileForDuplicate(null);
      setNewProfileData({ name: '', description: '', useCase: '' });
      loadProfiles();
    } catch (error) {
      console.error('Error duplicating profile:', error);
      toast.error('Failed to duplicate profile');
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile? This action cannot be undone.')) {
      return;
    }

    try {
      await LinkedInProfileService.deleteProfile(profileId);
      toast.success('Profile deleted successfully');
      loadProfiles();
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error('Failed to delete profile');
    }
  };

  const handleViewEditProfile = (profile: LinkedInProfileData) => {
    // Always navigate to the detail page since we only show profiles with content
    navigate(`/linkedin-profile/${profile.id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'ready_for_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'generated': return 'bg-emerald-100 text-emerald-800';
      case 'content_saved': return 'bg-purple-100 text-purple-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="LinkedIn Profile Library" backTo="/" />
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded"></div>
                    <div className="h-3 bg-muted rounded w-2/3"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="LinkedIn Profile Library" backTo="/">
        <Dialog open={showNewProfileDialog} onOpenChange={setShowNewProfileDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              New Profile
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New LinkedIn Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="profile-name">Profile Name *</Label>
                <Input
                  id="profile-name"
                  placeholder="e.g., Tech Job Search 2025"
                  value={newProfileData.name}
                  onChange={(e) => setNewProfileData({ ...newProfileData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="profile-description">Description</Label>
                <Textarea
                  id="profile-description"
                  placeholder="Brief description of this profile's purpose..."
                  value={newProfileData.description}
                  onChange={(e) => setNewProfileData({ ...newProfileData, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="use-case">Use Case</Label>
                <Select value={newProfileData.useCase} onValueChange={(value) => setNewProfileData({ ...newProfileData, useCase: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select use case" />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASES.map((useCase) => (
                      <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={handleCreateProfile} className="flex-1">
                  Create Profile
                </Button>
                <Button variant="outline" onClick={() => setShowNewProfileDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-6">
        {profiles.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <Users className="h-12 w-12 text-muted-foreground mx-auto" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No LinkedIn Profiles Yet</h3>
            <p className="text-muted-foreground mb-6">
              Create your first LinkedIn profile to get started with professional optimization.
            </p>
            <Button onClick={() => setShowNewProfileDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Profile
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <Card key={profile.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg leading-tight">
                        {profile.profile_name && profile.profile_name !== 'Untitled Profile' 
                          ? profile.profile_name 
                          : profile.target_job_titles 
                            ? `${profile.target_job_titles} Profile`
                            : `Profile ${formatDate(profile.created_at || '')}`}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={getStatusColor(profile.status || 'draft')}>
                          {profile.status === 'generated' ? 'Completed' : profile.status?.replace('_', ' ') || 'Draft'}
                        </Badge>
                        {profile.use_case && (
                          <Badge variant="outline" className="text-xs">
                            {profile.use_case}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {profile.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {profile.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {profile.industries && profile.industries.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        <span>{profile.industries.slice(0, 2).join(', ')}</span>
                        {profile.industries.length > 2 && <span>+{profile.industries.length - 2}</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(profile.updated_at || profile.created_at || '')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleViewEditProfile(profile)}
                      className="flex-1"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      View / Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedProfileForDuplicate(profile.id!);
                        setNewProfileData({ name: `${profile.profile_name} Copy`, description: '', useCase: profile.use_case || '' });
                        setShowDuplicateDialog(true);
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteProfile(profile.id!)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="duplicate-name">New Profile Name *</Label>
              <Input
                id="duplicate-name"
                placeholder="Enter name for the duplicated profile"
                value={newProfileData.name}
                onChange={(e) => setNewProfileData({ ...newProfileData, name: e.target.value })}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleDuplicateProfile} className="flex-1">
                Duplicate Profile
              </Button>
              <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}